import { GenerationType, ProcessingMode } from 'constants/visualStaging/visualStagingTypes'
import React, { useCallback } from 'react'

import { AnalyticsEvent } from 'utils/analytics'
import { Box } from '@mui/material'
import { EditionType } from '../../../ClientPhotoStudio.context'
import { NonSubscriberVirtualStagingUpsell } from './NonSubscriberVirtualStagingUpsell.component'
import { VirtualStagingPanel } from './VirtualStagingPanel.component'
import { VirtualStagingState } from 'models/visualStaging/virtualStagingTypes'
import { useClientPhotoStudio } from '../../../ClientPhotoStudio.context'
import { useVisualStagingActions } from '../../../VisualStaging/hooks/useVisualStagingActions.hook'
import { useWorkspacePlan } from 'utils/hooks'

interface VirtualStagingPanelContainerProps {
  virtualStagingState: VirtualStagingState
  setVirtualStagingState: (updater: (prev: VirtualStagingState) => VirtualStagingState) => void
  onGenerate?: () => void
  generateRef?: React.RefObject<{ handleMagicEraserGenerate: () => Promise<void> }>
  hasMask?: boolean
  isMaskRequired?: boolean
  canGenerate?: boolean
}

/**
 * VirtualStagingPanelContainer component for Virtual Staging.
 * Container component that integrates VirtualStagingPanel with useVisualStagingActions hook.
 * Provides history-enabled actions to all child components with undo/redo functionality.
 * Manages state updates and action dispatching for the Virtual Staging interface.
 * 
 * @example
 * <VirtualStagingPanelContainer
 *   virtualStagingState={virtualStagingState}
 *   setVirtualStagingState={setVirtualStagingState}
 * />
 */
export const VirtualStagingPanelContainer: React.FC<VirtualStagingPanelContainerProps> = ({
  virtualStagingState,
  setVirtualStagingState,
  onGenerate,
  generateRef,
  hasMask,
  isMaskRequired,
  canGenerate,
}) => {
  const {
    aiLabelState,
    setAiLabelToggle,
    setAiLabelType,
    setAiLabelText,
    setAiLabelColour,
    setAiLabelOpacity,
    resetAiLabelPlacement,
    isSaving,
    isProcessingImage,
    isOriginalVisualType,
    getCommonAnalyticsData,
    logPhotoStudioEvent
  } = useClientPhotoStudio()

  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  // Handle generate - check if mask is needed
  const handleGenerate = React.useCallback(() => {
    // For declutter, paint_walls, and change_floor, no mask is needed - call onGenerate directly
    const isDeclutterMode = virtualStagingState.generationType === GenerationType.DECLUTTER_ROOM
    const isPaintWallsMode = virtualStagingState.generationType === GenerationType.PAINT_WALLS
    const isChangeFloorMode = virtualStagingState.generationType === GenerationType.CHANGE_FLOOR

    if (isDeclutterMode || isPaintWallsMode || isChangeFloorMode) {
      // These modes don't use masks - call onGenerate directly without exporting mask
      if (onGenerate) {
        onGenerate()
      }
    } else if (virtualStagingState.processingMode === ProcessingMode.PRESERVE_ZONE ||
      virtualStagingState.processingMode === ProcessingMode.REPLACE_ZONE) {
      // If mask is needed (preserve/replace zone), use generateRef to export mask first
      if (generateRef?.current) {
        generateRef.current.handleMagicEraserGenerate()
      }
    } else {
      // Entire image mode - call onGenerate directly (no mask upload needed!!!)
      if (onGenerate) {
        onGenerate()
      }
    }
  }, [virtualStagingState.generationType, virtualStagingState.processingMode, generateRef, onGenerate])

  const {
    changeGenerationType: changeGenerationTypeBase,
    changeProcessingMode,
    changeRoomType,
    changeSelectedStyle,
    toggleFloorPreservation,
    changeWallColor: changeWallColorBase,
    changeFloorTemplate: changeFloorTemplateBase,
  } = useVisualStagingActions(virtualStagingState, setVirtualStagingState)

  // Wrapper for changeGenerationType with analytics tracking
  const changeGenerationType = useCallback((generationType: GenerationType) => {
    changeGenerationTypeBase(generationType)
    getCommonAnalyticsData(undefined, virtualStagingState.roomType || undefined).then(commonData => {
      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
        editionType: EditionType.VIRTUAL_STAGING,
        editionSettings: {
          generationType,
        },
        ...commonData,
      })
    })
  }, [changeGenerationTypeBase, getCommonAnalyticsData, logPhotoStudioEvent, virtualStagingState.roomType])

  // Wrapper for changeWallColor with analytics tracking
  const changeWallColor = useCallback((color: string) => {
    changeWallColorBase(color)
    // Track edition selected when wall color changes
    if (virtualStagingState.generationType === GenerationType.PAINT_WALLS) {
      getCommonAnalyticsData(undefined, undefined).then(commonData => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
          editionType: EditionType.VIRTUAL_STAGING,
          editionSettings: {
            generationType: GenerationType.PAINT_WALLS,
            wallColor: color,
          },
          ...commonData,
        })
      }).catch(() => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
          editionType: EditionType.VIRTUAL_STAGING,
          editionSettings: {
            generationType: GenerationType.PAINT_WALLS,
            wallColor: color,
          },
        })
      })
    }
  }, [changeWallColorBase, virtualStagingState.generationType, getCommonAnalyticsData, logPhotoStudioEvent])

  // Wrapper for changeFloorTemplate with analytics tracking
  const changeFloorTemplate = useCallback((template: string) => {
    changeFloorTemplateBase(template)
    // Track edition selected when floor template changes
    if (virtualStagingState.generationType === GenerationType.CHANGE_FLOOR) {
      getCommonAnalyticsData(undefined, undefined).then(commonData => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
          editionType: EditionType.VIRTUAL_STAGING,
          editionSettings: {
            generationType: GenerationType.CHANGE_FLOOR,
            floorTemplate: template,
          },
          ...commonData,
        })
      }).catch(() => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
          editionType: EditionType.VIRTUAL_STAGING,
          editionSettings: {
            generationType: GenerationType.CHANGE_FLOOR,
            floorTemplate: template,
          },
        })
      })
    }
  }, [changeFloorTemplateBase, virtualStagingState.generationType, getCommonAnalyticsData, logPhotoStudioEvent])

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      {/* SUBSCRIBERS ONLY SECTION */}
      {!hasWorkspaceActiveSubscription && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            paddingRight: '0.4rem', // small padding to avoid scrollbar overlap
          }}
        >
          <NonSubscriberVirtualStagingUpsell />
        </Box>
      )}

      {hasWorkspaceActiveSubscription && (
        <VirtualStagingPanel
          virtualStagingState={virtualStagingState}
          onGenerationTypeChange={changeGenerationType}
          onProcessingModeChange={changeProcessingMode}
          onRoomTypeChange={changeRoomType}
          onStyleChange={changeSelectedStyle}
          onFloorToggle={toggleFloorPreservation}
          onWallColorChange={changeWallColor}
          onFloorTemplateChange={changeFloorTemplate}
          aiLabelState={aiLabelState}
          onAiLabelToggle={setAiLabelToggle}
          onAiLabelTypeChange={setAiLabelType}
          onAiLabelTextChange={setAiLabelText}
          onAiLabelColourChange={setAiLabelColour}
          onAiLabelOpacityChange={setAiLabelOpacity}
          onAiLabelResetPlacement={resetAiLabelPlacement}
          isDisabled={virtualStagingState.isGeneratingStaging || isProcessingImage || isSaving || isOriginalVisualType}
          onGenerate={handleGenerate}
          isProcessingImage={isProcessingImage}
          hasMask={hasMask}
          isMaskRequired={isMaskRequired}
          canGenerate={canGenerate}
        />
      )}
    </Box>
  )
}
